import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { publicApi } from '../../api/client'
import type { EventItem, SiteNewsItem, SiteVideoBand } from '../../types/api'
import { getEventPrimaryActionLabel } from '../../utils/eventLabels'
import { DesktopNav, MobileNav, SectionHeading, SiteFooter } from './SiteChrome'
import { useScrollToAnchor } from './useScrollToAnchor'
import { donatePaymentUrl, ticketMarketplacePath } from './siteMenu'
import { fallbackNews, newsDetailPath } from './newsData'

type CtaTile = {
  label: string
  lines: string[]
  href: string
  color: string
}

const landscapeImageFallbacks: Record<string, string> = {
  'storytelling-masterclass-with-uncle-ebo-whyte': '/assets/STORYTELLING.jpg',
}
type FeaturedEvent = EventItem & { slideImage: string }

const defaultVideoBand: SiteVideoBand = {
  active: true,
  title: 'African Art Network video',
  videoUrl: '',
  posterImage: '',
  embedUrl:
    'https://player.vimeo.com/video/1104958818?autoplay=1&playsinline=1&loop=1&muted=1&title=0&portrait=0&byline=0',
}

const fallbackEvents = [
  {
    src: '/assets/event-black-star.jpg',
    alt: 'The Black Star stage play event poster',
    href: ticketMarketplacePath,
    title: 'The Black Star',
    meta: 'A Stage Play',
  },
  {
    src: '/assets/event-call-actors.jpg',
    alt: 'Call for actors poster',
    href: ticketMarketplacePath,
    title: 'Call for Actors',
    meta: 'AAN Auditions',
  },
]

const ctaTiles: CtaTile[] = [
  {
    label: 'AAN EDUC8:',
    lines: ['EMPOWERING', 'THE FUTURE', 'OF ARTS'],
    href: '/projects/aan-educ8',
    color: '#eb3443',
  },
  {
    label: 'AAN PRODUCTIONS:',
    lines: ['EXPERIENCE', 'AFRICAN FILM &', 'THEATRE', 'EXCELLENCE'],
    href: '/projects/aan-productions',
    color: '#f0b84a',
  },
  {
    label: 'AAN VOLUNTEERS:',
    lines: ['MAKE A', 'DIFFERENCE', 'WITH AAN'],
    href: '/join/volunteers',
    color: '#4fdf5b',
  },
  {
    label: 'AAN TALENTS:',
    lines: ['EXPLORE', 'AMAZING AFRICAN', 'TALENTS'],
    href: '/projects/talent-africa',
    color: '#d9ef00',
  },
]

function Hero() {
  return (
    <header
      className="relative h-[126px] overflow-visible bg-black bg-cover bg-center sm:h-[190px] lg:h-[560px]"
      style={{ backgroundImage: "url('/assets/hero-bg.jpeg')" }}
    >
      <div className="mx-auto flex h-full max-w-7xl items-start justify-between px-4 pt-3 sm:px-8 lg:block lg:pt-0">
        <div className="lg:absolute lg:left-[11%] lg:top-[34%]">
          <Link to="/" aria-label="African Art Network home">
            <img
              className="w-20 sm:w-32 lg:w-[470px]"
              src="/assets/logo-white.png"
              alt="African Art Network"
            />
          </Link>
          <div className="mt-7 hidden lg:block">
            <DesktopNav />
          </div>
        </div>
        <MobileNav />
      </div>
    </header>
  )
}

function PromoSlider() {
  const [events, setEvents] = useState<FeaturedEvent[]>([])
  const [active, setActive] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    void publicApi
      .listEvents({ featured: true, period: 'upcoming' })
      .then(({ events: results }) => {
        if (!mounted) return
        setEvents(
          results.flatMap((event) => {
            const slideImage = event.landscapeImage?.trim() || landscapeImageFallbacks[event.slug] || ''
            return slideImage ? [{ ...event, slideImage }] : []
          })
        )
        setActive(0)
      })
      .catch(() => {
        if (!mounted) return
        setEvents([])
      })
      .finally(() => {
        if (mounted) setIsLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (events.length < 2) return undefined

    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % events.length)
    }, 6000)

    return () => window.clearInterval(timer)
  }, [events.length])

  if (isLoading || events.length === 0) {
    return null
  }

  return (
    <section aria-label="Featured events" className="relative overflow-hidden bg-black">
      <div
        className="flex transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${active * 100}%)` }}
      >
        {events.map((event) => (
          <Link className="group relative block min-w-full" to={`/screening/${event.id}`} key={event.id}>
            <img className="aspect-[2.12/1] w-full object-cover" src={event.slideImage} alt={event.title} />
            <span className="absolute bottom-6 right-6 inline-flex min-h-12 items-center justify-center rounded-full bg-aan-bright px-7 font-display text-sm font-black uppercase tracking-[0.1em] text-black shadow-glow transition group-hover:bg-white sm:bottom-8 sm:right-8">
              {getEventPrimaryActionLabel(event)}
            </span>
          </Link>
        ))}
      </div>
      {events.length > 1 ? (
        <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5">
          {events.map((event, index) => (
            <button
              aria-label={`Show ${event.title}`}
              className={`h-2.5 w-2.5 rounded-full border border-white/80 ${
                active === index ? 'bg-white' : 'bg-white/35'
              }`}
              key={event.id}
              onClick={() => setActive(index)}
              type="button"
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}

function VideoBand() {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [videoBand, setVideoBand] = useState<SiteVideoBand>(defaultVideoBand)
  const [isMuted, setIsMuted] = useState(true)

  useEffect(() => {
    let active = true

    void publicApi
      .getSiteContent()
      .then(({ content }) => {
        if (active) setVideoBand(content.videoBand)
      })
      .catch(() => undefined)

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!videoRef.current) return
    videoRef.current.muted = isMuted
    videoRef.current.volume = isMuted ? 0 : 1
  }, [isMuted, videoBand.videoUrl])

  if (!videoBand.active) {
    return null
  }

  if (videoBand.videoUrl) {
    return (
      <section className="bg-black">
        <div className="relative aspect-video w-full overflow-hidden bg-black">
          <video
            ref={videoRef}
            aria-label={videoBand.title}
            autoPlay
            className="absolute inset-0 h-full w-full object-cover"
            loop
            muted={isMuted}
            playsInline
            poster={videoBand.posterImage || undefined}
            preload="auto"
            src={videoBand.videoUrl}
          />
          <button
            aria-label={isMuted ? `Unmute ${videoBand.title}` : `Mute ${videoBand.title}`}
            className="absolute left-1/2 top-5 z-10 min-h-12 -translate-x-1/2 rounded-[8px] bg-black px-6 font-display text-base font-black text-white transition hover:bg-aan-bright hover:text-black focus:outline-none focus:ring-2 focus:ring-aan-bright"
            onClick={() => setIsMuted((current) => !current)}
            type="button"
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="bg-black">
      <div className="relative aspect-video w-full overflow-hidden bg-black">
        <iframe
          allow="autoplay; fullscreen; picture-in-picture"
          className="absolute inset-0 h-full w-full border-0"
          src={videoBand.embedUrl || defaultVideoBand.embedUrl}
          title={videoBand.title}
        />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 lg:hidden">
          <span className="h-12 w-12 animate-spin rounded-full border-2 border-white/90 border-t-transparent" />
        </div>
      </div>
    </section>
  )
}

function CtaTileCard({ tile }: { tile: CtaTile }) {
  return (
    <Link
      className="group flex min-h-[300px] flex-col items-start justify-center rounded-[20px] border-[3px] bg-black px-10 py-20 transition-transform hover:-translate-y-1 lg:min-h-[360px]"
      to={tile.href}
      style={{ borderColor: tile.color, color: tile.color }}
    >
      <h2 className="font-display text-[30px] font-semibold uppercase leading-[30px]">
        <span>{tile.label}</span>
        <br />
        {tile.lines.map((line) => (
          <span key={line}>
            {line}
            <br />
          </span>
        ))}
      </h2>
      <span className="mt-6 rounded-full bg-white px-[30px] py-2 font-aan text-base font-medium uppercase text-neutral-800 transition group-hover:bg-aan-bright group-hover:text-black">
        Click Here
      </span>
    </Link>
  )
}

function CtaGrid() {
  const gallery = ['/assets/gallery-web-2.jpg', '/assets/gallery-web-1.jpg', '/assets/gallery-web-7.jpg']
  const [galleryIndex, setGalleryIndex] = useState(0)

  useEffect(() => {
    const timer = window.setInterval(() => {
      setGalleryIndex((current) => (current + 1) % gallery.length)
    }, 4500)

    return () => window.clearInterval(timer)
  }, [gallery.length])

  return (
    <section className="bg-black px-4 py-12 sm:px-7 sm:py-16 lg:py-24">
      <div className="mx-auto grid max-w-[1160px] gap-6 lg:grid-cols-[1.08fr_0.92fr]">
        <div className="grid gap-6 sm:grid-cols-2">
          {ctaTiles.map((tile) => (
            <CtaTileCard tile={tile} key={tile.label} />
          ))}
        </div>
        <div
          aria-label="African Art Network gallery"
          className="hidden min-h-[746px] rounded-[8px] border border-white/35 bg-cover bg-center transition-[background-image] duration-700 lg:block"
          role="img"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.18)), url('${gallery[galleryIndex]}')`,
          }}
        />
      </div>
    </section>
  )
}

function Events() {
  const [events, setEvents] = useState<EventItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasLoadedFromApi, setHasLoadedFromApi] = useState(false)
  const eventGridClass =
    events.length === 1
      ? 'mx-auto grid max-w-md gap-5 px-0 pb-10 sm:px-6 lg:pb-20'
      : 'mx-auto grid max-w-md gap-5 px-0 pb-10 sm:px-6 lg:max-w-6xl lg:grid-cols-3 lg:gap-8 lg:pb-20'

  useEffect(() => {
    let active = true

    void publicApi
      .listEvents({ period: 'upcoming' })
      .then(({ events: results }) => {
        if (!active) return
        setEvents(results)
        setHasLoadedFromApi(true)
      })
      .catch(() => {
        if (!active) return
        setEvents([])
        setHasLoadedFromApi(false)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  return (
    <section id="events" className="bg-black">
      <div className="py-12 sm:py-16">
        <SectionHeading>Upcoming Events</SectionHeading>
      </div>

      {isLoading ? (
        <div className="mx-auto max-w-md px-6 pb-20 text-center font-display text-sm font-semibold uppercase tracking-[0.24em] text-white/60">
          Loading events...
        </div>
      ) : hasLoadedFromApi && events.length > 0 ? (
        <div className={eventGridClass}>
          {events.map((event) => (
            <Link
              className="group block overflow-hidden rounded-[8px] border border-white/15 bg-black transition hover:-translate-y-1 hover:border-aan-bright"
              to={`/screening/${event.id}`}
              key={event.id}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-black">
                <img
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  src={event.image}
                  alt={event.title}
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/75 to-transparent p-5">
                  <div className="font-display text-[11px] font-semibold uppercase tracking-[0.24em] text-aan-bright">
                    {event.category}
                  </div>
                  <h3 className="mt-2 font-display text-2xl font-black uppercase leading-none text-white">
                    {event.title}
                  </h3>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/80">
                    {event.date} / {event.time} / {event.city}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between px-5 py-4">
                <span className="text-xs font-semibold text-white/70">{event.venue}</span>
                <span className="rounded-full bg-white px-4 py-1.5 font-display text-[10px] font-black uppercase text-black transition group-hover:bg-aan-bright">
                  {getEventPrimaryActionLabel(event)}
                </span>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="mx-auto grid max-w-md gap-5 px-0 pb-10 sm:px-6 lg:max-w-5xl lg:grid-cols-2 lg:gap-8 lg:pb-20">
          {fallbackEvents.map((event) => (
            <Link className="block overflow-hidden bg-black transition hover:scale-[1.01]" to={event.href} key={event.src}>
              <img className="w-full" src={event.src} alt={event.alt} />
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}

function AdinkraExperience() {
  return (
    <section id="adinkra" className="bg-black">
      <Link to="/projects/adinkra-experience">
        <img
          className="h-auto w-full"
          src="/assets/adinkra-experience.jpg"
          alt="Adinkra Experience - Your online African marketplace"
        />
      </Link>
    </section>
  )
}

function News() {
  const [items, setItems] = useState<SiteNewsItem[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let active = true

    void publicApi
      .getSiteContent()
      .then(({ content }) => {
        if (active) setItems(content.news)
      })
      .catch(() => {
        if (active) setItems(fallbackNews)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  const visibleNews = items.filter((item) => item.published)

  if (!isLoading && visibleNews.length === 0) {
    return null
  }

  return (
    <section id="news" className="bg-black px-6 py-12 sm:py-16 lg:py-20">
      <SectionHeading>News</SectionHeading>
      {isLoading ? (
        <div className="mx-auto mt-14 max-w-md text-center font-display text-sm font-semibold uppercase tracking-[0.24em] text-white/60">
          Loading news...
        </div>
      ) : (
        <div className="mx-auto mt-16 grid max-w-[1100px] gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {visibleNews.map((item) => {
            const detailPath = newsDetailPath(item.slug)
            const preview = item.excerpt || item.body
            return (
              <article className="text-left" key={item.slug}>
                <Link className="group block overflow-hidden rounded-[8px] bg-neutral-900" to={detailPath}>
                  {item.image ? (
                    <img
                      className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-105"
                      src={item.image}
                      alt={item.imageAlt || item.title}
                    />
                  ) : (
                    <div className="flex aspect-[4/3] items-center justify-center bg-neutral-900 px-6 text-center font-display text-xs font-black uppercase tracking-[0.18em] text-white/50">
                      African Art Network
                    </div>
                  )}
                </Link>
                <h3 className="mt-5 font-display text-[18px] font-black leading-[1.08] text-white lg:text-[16px]">
                  <Link className="transition hover:text-aan-bright" to={detailPath}>
                    {item.title}
                  </Link>
                </h3>
                {preview ? <p className="mt-5 text-[12px] leading-relaxed text-white/70">{preview}</p> : null}
                <Link
                  className="mt-5 inline-flex min-h-10 items-center justify-center rounded-full border border-aan-bright px-5 font-display text-[11px] font-black uppercase tracking-[0.14em] text-aan-bright transition hover:bg-aan-bright hover:text-black"
                  to={detailPath}
                >
                  Read more
                </Link>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}

function Donate() {
  return (
    <section id="donate" className="bg-black px-6 py-14 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[1080px]">
        <SectionHeading>Donate</SectionHeading>
        <div className="mt-14 grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-stretch">
          <div className="border-l-4 border-aan-bright pl-6">
            <p className="font-display text-[17px] font-black uppercase leading-tight text-aan-bright sm:text-[22px]">
              Support the future of arts in Africa
            </p>
            <div className="mt-6 space-y-5 text-sm leading-7 text-white/80 sm:text-base">
              <p>
                The Africa Arts Development Fund supports artists, cultural workers, and creative institutions across
                the continent through funding, opportunities, and development programs.
              </p>
              <p>
                Your donation helps sustain creative education, artist support, cultural exchange, and platforms that
                connect African talent with wider audiences.
              </p>
            </div>
            <Link
              className="mt-7 inline-flex items-center justify-center rounded-full border border-aan-bright px-7 py-3 font-display text-xs font-black uppercase tracking-[0.14em] text-aan-bright transition hover:bg-aan-bright hover:text-black"
              to="/projects/aad-fund"
            >
              Read more about AADF
            </Link>
          </div>
          <div className="flex flex-col justify-center rounded-[8px] border border-aan-bright bg-aan-footer px-7 py-9 text-center shadow-glow sm:px-10">
            <p className="font-display text-[13px] font-semibold uppercase tracking-[0.24em] text-white/70">
              Africa Arts Development Fund
            </p>
            <h3 className="mt-4 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl">
              Donate to AAD Fund
            </h3>
            <p className="mx-auto mt-5 max-w-sm text-sm leading-6 text-white/75">
              Continue to the secure Paystack donation page to make your contribution.
            </p>
            <a
              className="mx-auto mt-8 inline-flex min-h-12 items-center justify-center rounded-full bg-aan-bright px-8 font-display text-sm font-black uppercase tracking-[0.12em] text-black transition hover:bg-white"
              href={donatePaymentUrl}
              rel="noreferrer"
              target="_blank"
            >
              Donate Now
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

function AANWebsitePage() {
  useScrollToAnchor()

  return (
    <main className="aan-site min-h-screen bg-black text-white">
      <Hero />
      <section className="bg-aan-green py-3 text-center font-display text-[11px] font-bold uppercase tracking-[0.72em] text-white sm:text-[14px] lg:py-4 lg:text-[18px]">
        Promoting the African Experience
      </section>
      <PromoSlider />
      <VideoBand />
      <CtaGrid />
      <section id="aecinemax" className="bg-black">
        <Link to="/projects/aecinemax">
          <img className="h-auto w-full" src="/assets/aecinemax.jpg" alt="AECinemax" />
        </Link>
      </section>
      <Events />
      <AdinkraExperience />
      <News />
      <Donate />
      <SiteFooter />
    </main>
  )
}

export default AANWebsitePage
